import { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { User, Bell, Search, X, Check, Star, Crown, Lock, Loader2 } from "lucide-react";
import { useUserFavorites, AVAILABLE_LEAGUES, PLAYER_LIMITS, type FavoritePlayer } from "@/hooks/useUserFavorites";
import { useUserFavoritesSync } from "@/hooks/useUserFavoritesSync";
import { NHL_ROSTERS, getNHLHeadshotUrl } from "@/data/nhlRosters";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";

interface FavoritePlayersModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Flatten all NHL players from rosters
const getAllNHLPlayers = (): FavoritePlayer[] => {
  const players: FavoritePlayer[] = [];
  
  Object.entries(NHL_ROSTERS).forEach(([teamKey, roster]) => {
    const teamAbbr = teamKey.replace("NHL-", "");
    
    [...roster.forwards, ...roster.defense, ...roster.goalies].forEach((player) => {
      players.push({
        id: player.id,
        name: player.name,
        team: teamAbbr,
        league: "NHL",
        position: player.position,
        number: player.number,
        photo: player.nhlId ? getNHLHeadshotUrl(player.nhlId) : undefined,
        nationality: player.nationality,
      });
    });
  });
  
  return players;
};

// Comprehensive player data for European leagues
const getSamplePlayers = (league: string): FavoritePlayer[] => {
  const leaguePlayers: Record<string, FavoritePlayer[]> = {
    SHL: [
      // FHC (Frölunda)
      { id: 100, name: "Max Friberg", team: "FHC", league: "SHL", position: "C", nationality: "SWE" },
      { id: 101, name: "Ryan Lasch", team: "FHC", league: "SHL", position: "RW", nationality: "USA" },
      { id: 1006, name: "Viggo Björk", team: "FHC", league: "SHL", position: "RW", nationality: "SWE" },
      { id: 1007, name: "Carl Grundström", team: "FHC", league: "SHL", position: "LW", nationality: "SWE" },
      { id: 1008, name: "Emil Bemström", team: "FHC", league: "SHL", position: "RW", nationality: "SWE" },
      { id: 108, name: "Lucas Raymond", team: "FHC", league: "SHL", position: "LW", nationality: "SWE" },
      { id: 109, name: "Rickard Rakell", team: "FHC", league: "SHL", position: "RW", nationality: "SWE" },
      // DIF (Djurgården)
      { id: 120, name: "Jacob Josefson", team: "DIF", league: "SHL", position: "C", nationality: "SWE" },
      { id: 127, name: "Patric Hornqvist", team: "DIF", league: "SHL", position: "RW", nationality: "SWE" },
      { id: 1204, name: "Jonathan Dahlén", team: "DIF", league: "SHL", position: "LW", nationality: "SWE" },
      { id: 1205, name: "William Nylander Altelius", team: "DIF", league: "SHL", position: "C", nationality: "SWE" },
      { id: 1206, name: "Nikolas Brouillard", team: "DIF", league: "SHL", position: "RD", nationality: "CAN" },
      // LHF (Luleå)
      { id: 140, name: "Oscar Lindberg", team: "LHF", league: "SHL", position: "C", nationality: "SWE" },
      { id: 155, name: "Jesper Wallstedt", team: "LHF", league: "SHL", position: "G", nationality: "SWE" },
      // RBK (Rögle)
      { id: 2020, name: "Simon Edvinsson", team: "RBK", league: "SHL", position: "LD", nationality: "SWE" },
      { id: 2006, name: "Marco Kasper", team: "RBK", league: "SHL", position: "C", nationality: "AUT" },
      // SKE (Skellefteå)
      { id: 162, name: "Emil Bemström", team: "SKE", league: "SHL", position: "RW", nationality: "SWE" },
      // VLH (Växjö)
      { id: 182, name: "Elias Pettersson", team: "VLH", league: "SHL", position: "C", nationality: "SWE" },
    ],
    KHL: [
      // SKA
      { id: 200, name: "Nikita Gusev", team: "SKA", league: "KHL", position: "LW", nationality: "RUS" },
      { id: 201, name: "Vadim Shipachyov", team: "SKA", league: "KHL", position: "C", nationality: "RUS" },
      { id: 202, name: "Matvei Michkov", team: "SKA", league: "KHL", position: "RW", nationality: "RUS" },
      { id: 203, name: "Ivan Demidov", team: "SKA", league: "KHL", position: "LW", nationality: "RUS" },
      // CSKA
      { id: 204, name: "Anton Slepyshev", team: "CSKA", league: "KHL", position: "LW", nationality: "RUS" },
      { id: 205, name: "Sergei Fedorov Jr", team: "CSKA", league: "KHL", position: "C", nationality: "RUS" },
      // AVT (Avtomobilist)
      { id: 2044, name: "Konstantin Okulov", team: "AVT", league: "KHL", position: "LW", nationality: "RUS" },
      { id: 2045, name: "Nigel Dawes", team: "AVT", league: "KHL", position: "RW", nationality: "CAN" },
      { id: 2046, name: "Artyom Anisimov", team: "AVT", league: "KHL", position: "C", nationality: "RUS" },
      { id: 2052, name: "Kirill Semyonov", team: "AVT", league: "KHL", position: "LD", nationality: "RUS" },
      { id: 2053, name: "Evgeny Biryukov", team: "AVT", league: "KHL", position: "RD", nationality: "RUS" },
      { id: 2056, name: "Mikhail Berdin", team: "AVT", league: "KHL", position: "G", nationality: "RUS" },
      // SAL (Salavat Yulaev)
      { id: 2080, name: "Sakari Manninen", team: "SAL", league: "KHL", position: "C", nationality: "FIN" },
    ],
    Liiga: [
      // TPS
      { id: 300, name: "Mikael Granlund", team: "TPS", league: "Liiga", position: "C", nationality: "FIN" },
      { id: 302, name: "Kaapo Kakko", team: "TPS", league: "Liiga", position: "RW", nationality: "FIN" },
      // TAP (Tappara)
      { id: 321, name: "Anton Lundell", team: "TAP", league: "Liiga", position: "C", nationality: "FIN" },
      { id: 322, name: "Joakim Kemell", team: "TAP", league: "Liiga", position: "RW", nationality: "FIN" },
      { id: 323, name: "Patrik Laine", team: "TAP", league: "Liiga", position: "RW", nationality: "FIN" },
      // HIFK
      { id: 3000, name: "Teemu Hartikainen", team: "HIFK", league: "Liiga", position: "RW", nationality: "FIN" },
      { id: 3005, name: "Atte Ohtamaa", team: "HIFK", league: "Liiga", position: "LW", nationality: "FIN" },
      { id: 3006, name: "Joonas Nättinen", team: "HIFK", league: "Liiga", position: "C", nationality: "FIN" },
      { id: 3007, name: "Roope Hintz", team: "HIFK", league: "Liiga", position: "C", nationality: "FIN" },
      { id: 3011, name: "Miro Heiskanen", team: "HIFK", league: "Liiga", position: "RD", nationality: "FIN" },
      { id: 3012, name: "Henri Jokiharju", team: "HIFK", league: "Liiga", position: "RD", nationality: "FIN" },
      { id: 3013, name: "Sami Niku", team: "HIFK", league: "Liiga", position: "LD", nationality: "FIN" },
      { id: 3016, name: "Kevin Lankinen", team: "HIFK", league: "Liiga", position: "G", nationality: "FIN" },
      // JYP
      { id: 340, name: "Brad Lambert", team: "JYP", league: "Liiga", position: "C", nationality: "FIN" },
    ],
    DEL: [
      { id: 400, name: "Marcel Noebels", team: "EBB", league: "DEL", position: "C", nationality: "GER" },
      { id: 401, name: "Leo Pföderl", team: "EBB", league: "DEL", position: "RW", nationality: "GER" },
      { id: 402, name: "Lean Bergmann", team: "EBB", league: "DEL", position: "C", nationality: "GER" },
      { id: 4040, name: "Tim Stützle", team: "MAN", league: "DEL", position: "C", nationality: "GER" },
      { id: 4060, name: "Moritz Seider", team: "RED", league: "DEL", position: "RD", nationality: "GER" },
    ],
    NL: [
      // ZSC Lions
      { id: 500, name: "Denis Hollenstein", team: "ZSC", league: "NL", position: "LW", nationality: "SUI" },
      { id: 501, name: "Sven Andrighetto", team: "ZSC", league: "NL", position: "RW", nationality: "SUI" },
      { id: 502, name: "Nico Hischier", team: "ZSC", league: "NL", position: "C", nationality: "SUI" },
      { id: 503, name: "Timo Meier", team: "ZSC", league: "NL", position: "RW", nationality: "SUI" },
      { id: 504, name: "Denis Malgin", team: "ZSC", league: "NL", position: "C", nationality: "SUI" },
      { id: 506, name: "Willy Riedi", team: "ZSC", league: "NL", position: "RW", nationality: "SUI" },
      { id: 507, name: "Justin Sigrist", team: "ZSC", league: "NL", position: "C", nationality: "SUI" },
      { id: 508, name: "Roman Wick", team: "ZSC", league: "NL", position: "LW", nationality: "SUI" },
      { id: 514, name: "Mirco Müller", team: "ZSC", league: "NL", position: "RD", nationality: "SUI" },
      { id: 517, name: "Yannick Weber", team: "ZSC", league: "NL", position: "LD", nationality: "SUI" },
      // SCB (SC Bern)
      { id: 520, name: "Tristan Scherwey", team: "SCB", league: "NL", position: "RW", nationality: "SUI" },
      { id: 521, name: "Nino Niederreiter", team: "SCB", league: "NL", position: "RW", nationality: "SUI" },
      { id: 535, name: "Leonardo Genoni", team: "SCB", league: "NL", position: "G", nationality: "SUI" },
    ],
    Extraliga: [
      // SPA (Sparta Praha)
      { id: 600, name: "David Krejčí", team: "SPA", league: "Extraliga", position: "C", nationality: "CZE" },
      { id: 601, name: "Michal Řepík", team: "SPA", league: "Extraliga", position: "LW", nationality: "CZE" },
      { id: 602, name: "Jakub Vrána", team: "SPA", league: "Extraliga", position: "LW", nationality: "CZE" },
      { id: 606, name: "Ondřej Najman", team: "SPA", league: "Extraliga", position: "LW", nationality: "CZE" },
      { id: 607, name: "Tomáš Hyka", team: "SPA", league: "Extraliga", position: "C", nationality: "CZE" },
      { id: 608, name: "Filip Chlapík", team: "SPA", league: "Extraliga", position: "C", nationality: "CZE" },
      { id: 611, name: "David Jiříček", team: "SPA", league: "Extraliga", position: "RD", nationality: "CZE" },
      { id: 614, name: "David Němeček", team: "SPA", league: "Extraliga", position: "RD", nationality: "CZE" },
      { id: 615, name: "Tomáš Dvořák", team: "SPA", league: "Extraliga", position: "LD", nationality: "CZE" },
      // TRI (Oceláři Třinec)
      { id: 6000, name: "Marko Daňo", team: "TRI", league: "Extraliga", position: "LW", nationality: "SVK" },
      { id: 6001, name: "Martin Růžička", team: "TRI", league: "Extraliga", position: "RW", nationality: "CZE" },
      // KLA (Kladno)
      { id: 6202, name: "Jaromír Jágr", team: "KLA", league: "Extraliga", position: "RW", nationality: "CZE" },
    ],
    AHL: [
      { id: 700, name: "T.J. Tynan", team: "COA", league: "AHL", position: "C", nationality: "USA" },
      { id: 701, name: "Martin Frk", team: "COA", league: "AHL", position: "RW", nationality: "CZE" },
      { id: 702, name: "Shane Wright", team: "COA", league: "AHL", position: "C", nationality: "CAN" },
    ],
    ECHL: [
      { id: 800, name: "Josh Wilkins", team: "UTG", league: "ECHL", position: "C", nationality: "USA" },
      { id: 801, name: "Dylan Sikura", team: "UTG", league: "ECHL", position: "LW", nationality: "CAN" },
    ],
    Allsvenskan: [
      { id: 900, name: "Fabian Lysell", team: "LUL", league: "Allsvenskan", position: "RW", nationality: "SWE" },
      { id: 901, name: "Noah Ostlund", team: "AIK", league: "Allsvenskan", position: "C", nationality: "SWE" },
    ],
  };
  
  return leaguePlayers[league] || [];
};

// Flag mapping for nationalities
const NATIONALITY_FLAGS: Record<string, string> = {
  USA: "🇺🇸", CAN: "🇨🇦", SWE: "🇸🇪", FIN: "🇫🇮", RUS: "🇷🇺",
  CZE: "🇨🇿", GER: "🇩🇪", SUI: "🇨🇭", SVK: "🇸🇰", LAT: "🇱🇻",
  AUT: "🇦🇹", DEN: "🇩🇰", NOR: "🇳🇴", FRA: "🇫🇷", GBR: "🇬🇧",
};

export function FavoritePlayersModal({ isOpen, onClose }: FavoritePlayersModalProps) {
  const {
    favorites,
    addPlayer,
    removePlayer,
    togglePlayerAlerts,
    isFavoritePlayer,
    getPlayerLimit,
    isAtPlayerLimit,
  } = useUserFavorites();
  
  // Import auth context and sync hook
  const { user, subscription } = useAuth();
  const { savePlayers, isSaving } = useUserFavoritesSync();
  
  const canEdit = subscription.subscribed && (subscription.tier === "pro" || subscription.tier === "elite");
  const isMobile = useIsMobile();
  
  // Calculate player limit based on subscription
  const playerLimit = getPlayerLimit(subscription);
  const atLimit = isAtPlayerLimit(playerLimit);

  const [selectedLeagueTab, setSelectedLeagueTab] = useState<string>("NHL");
  const [playerSearch, setPlayerSearch] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  // Track changes when modal opens
  useEffect(() => {
    if (isOpen) {
      setHasChanges(false);
    }
  }, [isOpen]);

  // Get all players for selected league
  const playersForSelectedLeague = useMemo(() => {
    let players: FavoritePlayer[];
    
    if (selectedLeagueTab === "NHL") {
      players = getAllNHLPlayers();
    } else {
      players = getSamplePlayers(selectedLeagueTab);
    }
    
    if (!playerSearch) return players;
    
    const searchLower = playerSearch.toLowerCase();
    return players.filter(
      (p) =>
        p.name.toLowerCase().includes(searchLower) ||
        p.team.toLowerCase().includes(searchLower) ||
        p.position.toLowerCase().includes(searchLower) ||
        (p.nationality && p.nationality.toLowerCase().includes(searchLower))
    );
  }, [selectedLeagueTab, playerSearch]);

  const handlePlayerToggle = (player: FavoritePlayer) => {
    if (!canEdit) return;
    
    if (isFavoritePlayer(player.id, player.league)) {
      removePlayer(player.id, player.league);
    } else {
      // Check if at limit before adding
      if (atLimit) {
        return; // Don't add if at limit
      }
      addPlayer(player);
    }
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!user || !canEdit) return;
    
    await savePlayers(favorites.players);
    setHasChanges(false);
    onClose();
  };

  const PlayersContent = () => (
    <>
      <div className="flex-1 overflow-y-auto flex flex-col gap-4 pr-2">
        {/* League Tabs */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Star className="w-4 h-4 text-primary" />
            Select League
          </h3>
          <div className="flex gap-1 overflow-x-auto pb-2 scrollbar-thin">
            {AVAILABLE_LEAGUES.map((league) => (
              <button
                key={league.id}
                onClick={() => {
                  setSelectedLeagueTab(league.id);
                  setPlayerSearch("");
                }}
                disabled={!canEdit}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  selectedLeagueTab === league.id
                    ? "bg-primary text-primary-foreground"
                    : canEdit
                      ? "bg-secondary/50 text-muted-foreground hover:text-foreground"
                      : "bg-secondary/30 text-muted-foreground cursor-not-allowed"
                }`}
              >
                {league.flag} {league.id}
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="relative flex-shrink-0">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search players by name, team, or position..."
            value={playerSearch}
            onChange={(e) => setPlayerSearch(e.target.value)}
            className="pl-9 bg-secondary border-border"
          />
          {playerSearch && (
            <button
              onClick={() => setPlayerSearch("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
            </button>
          )}
        </div>

        {/* Players List */}
        <ScrollArea className="h-[25vh] pr-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {playersForSelectedLeague.slice(0, 100).map((player) => {
              const isSelected = isFavoritePlayer(player.id, player.league);
              const flag = player.nationality ? NATIONALITY_FLAGS[player.nationality] || "" : "";
              const isDisabled = (!isSelected && atLimit) || !canEdit;
              
              return (
                <button
                  key={`${player.league}-${player.id}`}
                  onClick={() => handlePlayerToggle(player)}
                  disabled={isDisabled}
                  className={`flex items-center gap-3 p-3 rounded-lg text-left transition-all ${
                    isSelected
                      ? "bg-primary/20 border border-primary/50 ring-1 ring-primary/30"
                      : isDisabled
                        ? "bg-secondary/30 border border-transparent opacity-50 cursor-not-allowed"
                        : "bg-secondary/50 border border-transparent hover:bg-secondary hover:border-border"
                  }`}
                >
                  {/* Player Photo */}
                  {player.photo ? (
                    <img
                      src={player.photo}
                      alt={player.name}
                      className="w-10 h-10 rounded-full object-cover bg-secondary"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "";
                        (e.target as HTMLImageElement).className = "hidden";
                      }}
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-xs font-bold text-muted-foreground">
                      {player.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate flex items-center gap-1.5">
                      {flag && <span>{flag}</span>}
                      {player.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {player.position} · {player.team} · {player.league}
                    </p>
                  </div>
                  
                  {isSelected ? (
                    <Star className="w-4 h-4 text-primary fill-primary flex-shrink-0" />
                  ) : isDisabled ? (
                    <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  ) : null}
                </button>
              );
            })}
          </div>
          
          {playersForSelectedLeague.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No players found matching your search</p>
            </div>
          )}
          
          {playersForSelectedLeague.length > 100 && (
            <p className="text-center text-xs text-muted-foreground mt-4">
              Showing first 100 results. Use search to find specific players.
            </p>
          )}
        </ScrollArea>

        {/* Selected Players Summary */}
        {favorites.players.length > 0 && (
          <div className="border-t border-border pt-4">
            <h4 className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
              Your Players ({favorites.players.length})
            </h4>
            <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto">
              {favorites.players.map((player) => (
                <Badge
                  key={`${player.league}-${player.id}`}
                  variant="secondary"
                  className="flex items-center gap-1.5 pr-1"
                >
                  <span>{player.name}</span>
                  <span className="text-muted-foreground">({player.team})</span>
                  <button
                    onClick={() => removePlayer(player.id, player.league)}
                    className="hover:text-destructive transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Notifications Toggle */}
        <div className="flex items-center justify-between py-3 px-4 bg-secondary/50 rounded-lg border border-border">
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-primary" />
            <div>
              <Label htmlFor="player-alerts-toggle" className="text-sm font-medium text-foreground">
                Enable player alerts
              </Label>
              <p className="text-xs text-muted-foreground">
                Get notified on goals, milestones, and game starts
              </p>
            </div>
          </div>
          <Switch
            id="player-alerts-toggle"
            checked={favorites.playerAlertsEnabled}
            onCheckedChange={togglePlayerAlerts}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 pt-4 border-t border-border flex-shrink-0">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
        {canEdit && (
          <Button onClick={handleSave} disabled={isSaving} className="gap-2">
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Save to Account
              </>
            )}
          </Button>
        )}
      </div>
    </>
  );

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader className="text-left">
            <DrawerTitle className="flex items-center gap-2 text-foreground">
              <User className="w-5 h-5 text-primary" />
              Follow Your Favorite Players
            </DrawerTitle>
            {/* Player Limit Indicator */}
            <div className="flex items-center gap-2 mt-2">
              <div className={`text-xs px-2 py-1 rounded-full ${
                atLimit 
                  ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" 
                  : "bg-primary/10 text-primary"
              }`}>
                {favorites.players.length} / {playerLimit} players
              </div>
              {atLimit && subscription.tier !== "elite" && (
                <span className="text-xs text-amber-400 flex items-center gap-1">
                  <Crown className="w-3 h-3" />
                  Upgrade to Elite for {PLAYER_LIMITS.elite} players
                </span>
              )}
            </div>
            {!canEdit && user && (
              <div className="flex items-center gap-2 mt-2 text-xs text-amber-400">
                <Lock className="w-3 h-3" />
                <span>Upgrade to Pro or Elite to customize your players</span>
                <Crown className="w-3 h-3" />
              </div>
            )}
          </DrawerHeader>
          <div className="overflow-y-auto px-4 pb-6 flex flex-col gap-4">
            <PlayersContent />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh] flex flex-col bg-background border-border">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <User className="w-5 h-5 text-primary" />
            Follow Your Favorite Players
          </DialogTitle>
          {/* Player Limit Indicator */}
          <div className="flex items-center gap-2 mt-2">
            <div className={`text-xs px-2 py-1 rounded-full ${
              atLimit 
                ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" 
                : "bg-primary/10 text-primary"
            }`}>
              {favorites.players.length} / {playerLimit} players
            </div>
            {atLimit && subscription.tier !== "elite" && (
              <span className="text-xs text-amber-400 flex items-center gap-1">
                <Crown className="w-3 h-3" />
                Upgrade to Elite for {PLAYER_LIMITS.elite} players
              </span>
            )}
          </div>
          {!canEdit && user && (
            <div className="flex items-center gap-2 mt-2 text-xs text-amber-400">
              <Lock className="w-3 h-3" />
              <span>Upgrade to Pro or Elite to customize your players</span>
              <Crown className="w-3 h-3" />
            </div>
          )}
        </DialogHeader>
        <PlayersContent />
      </DialogContent>
    </Dialog>
  );
}

export default FavoritePlayersModal;
